/**
 * Phase G+ Phase 5b (19.06.2026) — HR-Hub REBUILD
 * ===============================================
 * Simple, easy-to-understand 4-tab hub for vacation/sick/off management.
 *
 *   Tab 1 · Heute       — Live snapshot: who is absent today.
 *   Tab 2 · Anträge     — Pending approval queue (approve/reject inline).
 *   Tab 3 · Übersicht   — One row per employee with remaining vacation,
 *                         krank YTD, last absence; click → profile.
 *   Tab 4 · Migration   — Admin-only spreadsheet view to set the current
 *                         remaining vacation per employee in one go
 *                         (live switch from Transportlogy).
 *
 * Built to be operable by a non-power user. All UI in German.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePermissions } from "@/contexts/PermissionsContext";
import {
  CalendarDays, Plane, HeartPulse, Clock4, Users, Sparkles,
  Search, Loader2, Check, X as XIcon, ArrowRight, AlertCircle,
} from "lucide-react";

const TABS = [
  { key: "heute", label: "Heute", icon: CalendarDays },
  { key: "antraege", label: "Anträge", icon: AlertCircle },
  { key: "uebersicht", label: "Übersicht", icon: Users },
  { key: "migration", label: "Migration", icon: Sparkles, adminOnly: true },
];

const ABSENCE_LABELS = {
  urlaub: "Urlaub", krankenstand: "Krankenstand", zeitausgleich: "Frei",
  sonderurlaub: "Sonderurlaub", pflegeurlaub: "Pflegeurlaub",
  papamonat: "Papamonat", unbezahlt_frei: "Unbezahlt", unentschuldigt: "Unentsch.",
  karenz: "Karenz", feiertag: "Feiertag", kuendigungsfrist: "Kündigungsfrist",
};
const ABSENCE_TONES = {
  urlaub: "text-emerald-300 bg-emerald-500/10 border-emerald-500/40",
  krankenstand: "text-rose-300 bg-rose-500/10 border-rose-500/40",
  zeitausgleich: "text-sky-300 bg-sky-500/10 border-sky-500/40",
  sonderurlaub: "text-violet-300 bg-violet-500/10 border-violet-500/40",
  pflegeurlaub: "text-orange-300 bg-orange-500/10 border-orange-500/40",
  papamonat: "text-pink-300 bg-pink-500/10 border-pink-500/40",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-AT"); } catch { return iso; }
}

export default function HrAbsenceHub() {
  const { isAdmin } = usePermissions();
  const [tab, setTab] = useState("heute");
  const visibleTabs = TABS.filter(t => !t.adminOnly || isAdmin);

  return (
    <div className="space-y-4" data-testid="hr-hub">
      <div>
        <h1 className="font-display text-2xl font-bold text-zinc-50">Abwesenheiten</h1>
        <p className="text-xs text-zinc-500 mt-0.5">
          Urlaub · Krankenstand · Frei · Sonderfälle. Live aus dem Payroll-System.
        </p>
      </div>

      <div className="flex items-center gap-1 border-b border-zinc-800">
        {visibleTabs.map(t => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`hr-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-2 ${
                active ? "text-zinc-50 border-blue-600" : "text-zinc-400 hover:text-zinc-200 border-transparent"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "heute" && <TodayTab />}
      {tab === "antraege" && <RequestsTab />}
      {tab === "uebersicht" && <OverviewTab />}
      {tab === "migration" && isAdmin && <MigrationTab />}
    </div>
  );
}

// ─── Tab 1: Heute ──────────────────────────────────────────────────────────
function TodayTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/hr/today").then(r => setData(r.data)).catch(() => setData({ count: 0, items: [], by_type: {} }));
  }, []);
  if (!data) return <Spinner />;

  const byType = data.by_type || {};
  return (
    <div className="space-y-3" data-testid="hr-tab-heute-content">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Heute weg" value={data.count} icon={CalendarDays} tone="zinc" testid="hr-kpi-today-total" />
        <KPI label="Urlaub" value={byType.urlaub || 0} icon={Plane} tone="emerald" testid="hr-kpi-today-urlaub" />
        <KPI label="Krank" value={byType.krankenstand || 0} icon={HeartPulse} tone="rose" testid="hr-kpi-today-krank" />
        <KPI label="Frei" value={byType.zeitausgleich || 0} icon={Clock4} tone="sky" testid="hr-kpi-today-frei" />
      </div>

      {data.count === 0 ? (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-6 text-center text-zinc-500 text-sm">
          Heute ist niemand abwesend. ☀️
        </div>
      ) : (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-zinc-900/50 text-zinc-400">
                <th className="text-left px-3 py-2">Mitarbeiter</th>
                <th className="text-left px-3 py-2">Typ</th>
                <th className="text-left px-3 py-2">Von</th>
                <th className="text-left px-3 py-2">Bis</th>
                <th className="text-left px-3 py-2">Notiz</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((a) => (
                <tr key={a.id} className="border-t border-zinc-800 hover:bg-zinc-900/40">
                  <td className="px-3 py-2">
                    <Link to={`/mitarbeiter/${a.employee_id}`} className="text-zinc-100 hover:text-blue-400">
                      {a.employee_name || a.employee_id}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] ${ABSENCE_TONES[a.type] || "border-zinc-700 text-zinc-300"}`}>
                      {ABSENCE_LABELS[a.type] || a.type}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-zinc-300">{fmtDate(a.start_date)}</td>
                  <td className="px-3 py-2 text-zinc-300">{fmtDate(a.end_date)}</td>
                  <td className="px-3 py-2 text-zinc-400 max-w-md truncate">{a.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Tab 2: Anträge ────────────────────────────────────────────────────────
function RequestsTab() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("pending");

  const reload = () => {
    api.get(`/hr/absences?status=${filter}&limit=200`)
      .then(r => setItems(r.data?.items || r.data || []))
      .catch(() => setItems([]));
  };
  useEffect(() => { reload(); }, [filter]);

  if (items === null) return <Spinner />;
  return (
    <div className="space-y-3" data-testid="hr-tab-antraege-content">
      <div className="flex items-center gap-2">
        {["pending", "approved", "rejected"].map(s => (
          <button key={s}
                  onClick={() => setFilter(s)}
                  data-testid={`hr-req-filter-${s}`}
                  className={`text-xs px-3 py-1.5 rounded ${
                    filter === s ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                  }`}>
            {s === "pending" ? "Ausstehend" : s === "approved" ? "Genehmigt" : "Abgelehnt"}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-6 text-center text-zinc-500 text-sm">
          Keine {filter === "pending" ? "offenen Anträge" : "Einträge"}.
        </div>
      ) : (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-zinc-900/50 text-zinc-400">
                <th className="text-left px-3 py-2">Mitarbeiter</th>
                <th className="text-left px-3 py-2">Typ</th>
                <th className="text-left px-3 py-2">Zeitraum</th>
                <th className="text-right px-3 py-2">Tage</th>
                <th className="text-left px-3 py-2">Notiz</th>
                <th className="text-right px-3 py-2">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-zinc-800 hover:bg-zinc-900/40"
                    data-testid={`hr-req-row-${a.id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/mitarbeiter/${a.employee_id}`} className="text-zinc-100 hover:text-blue-400">
                      {a.employee_name || a.employee_id}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] ${ABSENCE_TONES[a.type] || "border-zinc-700 text-zinc-300"}`}>
                      {ABSENCE_LABELS[a.type] || a.type}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-zinc-300">{fmtDate(a.start_date)} → {fmtDate(a.end_date)}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{(a.working_days || 0).toFixed(1)}</td>
                  <td className="px-3 py-2 text-zinc-400 max-w-xs truncate">{a.note || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {filter === "pending" && (
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`hr-req-approve-${a.id}`}
                          onClick={async () => {
                            try { await api.post(`/hr/absences/${a.id}/approve`); toast.success("Genehmigt"); reload(); }
                            catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
                          }}
                          className="text-emerald-400 hover:text-emerald-300 p-1"
                          title="Genehmigen">
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`hr-req-reject-${a.id}`}
                          onClick={async () => {
                            const reason = prompt("Grund für Ablehnung?");
                            if (!reason) return;
                            try { await api.post(`/hr/absences/${a.id}/reject`, { reason }); toast.success("Abgelehnt"); reload(); }
                            catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
                          }}
                          className="text-rose-400 hover:text-rose-300 p-1"
                          title="Ablehnen">
                          <XIcon className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Tab 3: Übersicht ───────────────────────────────────────────────────────
function OverviewTab() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  useEffect(() => {
    api.get("/hr/overview").then(r => setData(r.data)).catch(() => setData({ items: [] }));
  }, []);
  const filtered = useMemo(() => {
    if (!data) return [];
    const s = search.toLowerCase();
    return data.items.filter(it =>
      !s || (it.employee_name || "").toLowerCase().includes(s)
    );
  }, [data, search]);

  if (!data) return <Spinner />;
  return (
    <div className="space-y-3" data-testid="hr-tab-uebersicht-content">
      <div className="flex items-center gap-2">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Mitarbeiter suchen…"
            data-testid="hr-overview-search"
            className="pl-7 pr-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-100 w-64"
          />
        </div>
        <div className="text-xs text-zinc-500">{filtered.length} von {data.count}</div>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-zinc-900/50 text-zinc-400">
              <th className="text-left px-3 py-2">Mitarbeiter</th>
              <th className="text-right px-3 py-2">Resturlaub</th>
              <th className="text-right px-3 py-2">Aufgebaut</th>
              <th className="text-right px-3 py-2">Anspruch</th>
              <th className="text-right px-3 py-2">Krank YTD</th>
              <th className="text-left px-3 py-2">Letzte Abwesenheit</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map((it) => (
              <tr key={it.employee_id} className="border-t border-zinc-800 hover:bg-zinc-900/40"
                  data-testid={`hr-overview-row-${it.employee_id}`}>
                <td className="px-3 py-2">
                  <Link to={`/mitarbeiter/${it.employee_id}`} className="text-zinc-100 hover:text-blue-400">
                    {it.employee_name}
                  </Link>
                  <div className="text-[10px] text-zinc-500">{it.role} · seit {fmtDate(it.entry_date)}</div>
                </td>
                <td className="px-3 py-2 text-right">
                  <span className={`font-mono ${it.remaining_now < 0 ? "text-rose-400" : it.remaining_now < 5 ? "text-amber-400" : "text-emerald-300"}`}>
                    {(it.remaining_now || 0).toFixed(1)}
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-zinc-400">{(it.accrued_to_date || 0).toFixed(1)}</td>
                <td className="px-3 py-2 text-right text-zinc-400">{(it.entitlement_total || 0).toFixed(0)}</td>
                <td className="px-3 py-2 text-right text-zinc-300">{(it.krank_ytd_days || 0).toFixed(1)}</td>
                <td className="px-3 py-2 text-zinc-500">
                  {it.last_absence ? (
                    <span>
                      <span className={`inline-flex items-center px-1.5 py-0 rounded border text-[10px] mr-1 ${ABSENCE_TONES[it.last_absence.type] || "border-zinc-700 text-zinc-400"}`}>
                        {ABSENCE_LABELS[it.last_absence.type] || it.last_absence.type}
                      </span>
                      {fmtDate(it.last_absence.start_date)}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  <Link to={`/mitarbeiter/${it.employee_id}`} className="text-zinc-500 hover:text-zinc-200">
                    <ArrowRight className="w-3.5 h-3.5 inline" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > 200 && (
          <div className="px-3 py-2 text-[11px] text-zinc-500 border-t border-zinc-800">
            … {filtered.length - 200} weitere Einträge. Bitte mit der Suche eingrenzen.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab 4: Migration ──────────────────────────────────────────────────────
function MigrationTab() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [drafts, setDrafts] = useState({});   // employee_id → string input
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("Migration aus Transportlogy");

  useEffect(() => {
    api.get("/hr/overview").then(r => {
      setData(r.data);
      // Pre-fill the drafts with current remaining so changes are clearer.
      const init = {};
      r.data.items.forEach(it => { init[it.employee_id] = (it.remaining_now ?? 0).toString(); });
      setDrafts(init);
    }).catch(() => setData({ items: [] }));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const s = search.toLowerCase();
    return data.items.filter(it => !s || (it.employee_name || "").toLowerCase().includes(s));
  }, [data, search]);

  const changedRows = useMemo(() => {
    if (!data) return [];
    return data.items
      .map(it => {
        const draft = (drafts[it.employee_id] ?? "").trim().replace(",", ".");
        const num = parseFloat(draft);
        if (isNaN(num) || num < 0) return null;
        if (Math.abs(num - (it.remaining_now || 0)) < 0.001) return null;
        return { employee_id: it.employee_id, remaining_days: num, name: it.employee_name };
      })
      .filter(Boolean);
  }, [data, drafts]);

  const submit = async () => {
    if (changedRows.length === 0) {
      toast.error("Keine Änderungen.");
      return;
    }
    if (!confirm(`${changedRows.length} Mitarbeiter migrieren? Dies friert deren monatlichen Aufbau für den aktuellen Zyklus ein.`)) return;
    setSaving(true);
    try {
      const res = await api.post("/hr/vacation/migrate-bulk", {
        rows: changedRows.map(r => ({ employee_id: r.employee_id, remaining_days: r.remaining_days })),
        note: note.trim() || null,
      });
      toast.success(`${res.data.succeeded} migriert · ${res.data.failed} Fehler`);
      // Reload to get fresh remaining values.
      const reloaded = await api.get("/hr/overview");
      setData(reloaded.data);
      const init = {};
      reloaded.data.items.forEach(it => { init[it.employee_id] = (it.remaining_now ?? 0).toString(); });
      setDrafts(init);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler bei Bulk-Migration");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <Spinner />;
  return (
    <div className="space-y-3" data-testid="hr-tab-migration-content">
      <div className="rounded-md border border-blue-500/40 bg-blue-500/10 p-3 text-xs text-blue-200">
        <b>Migration aus Transportlogy:</b> Trage hier ein, wie viele Urlaubstage jeder
        Mitarbeiter HEUTE noch übrig hat. Beim Speichern wird der Wert 1:1 übernommen
        und der monatliche Aufbau für den aktuellen Zyklus eingefroren. Du kannst
        auch nur einzelne Zeilen ändern — nur veränderte Zeilen werden gespeichert.
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Mitarbeiter suchen…"
                 data-testid="hr-migration-search"
                 className="pl-7 pr-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-100 w-64"/>
        </div>
        <input
          value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Notiz für alle (z.B. 'Aus Transportlogy 19.06.2026')"
          className="flex-1 min-w-[200px] px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-100"
        />
        <div className="text-xs text-zinc-400">
          <b className="text-amber-300">{changedRows.length}</b> geänderte Zeilen
        </div>
        <button
          data-testid="hr-migration-save"
          onClick={submit} disabled={saving || changedRows.length === 0}
          className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-xs px-3 py-1.5 rounded">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {changedRows.length === 0 ? "Keine Änderungen" : `${changedRows.length} migrieren`}
        </button>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-zinc-900/50 text-zinc-400">
              <th className="text-left px-3 py-2">Mitarbeiter</th>
              <th className="text-right px-3 py-2">Aktuell im System</th>
              <th className="text-right px-3 py-2">Anspruch</th>
              <th className="text-right px-3 py-2">Neuer Resturlaub</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 300).map((it) => {
              const draft = drafts[it.employee_id] ?? "";
              const num = parseFloat(draft.replace(",", "."));
              const changed = !isNaN(num) && Math.abs(num - (it.remaining_now || 0)) > 0.001;
              return (
                <tr key={it.employee_id}
                    className={`border-t border-zinc-800 ${changed ? "bg-amber-500/5" : ""}`}
                    data-testid={`hr-migration-row-${it.employee_id}`}>
                  <td className="px-3 py-2 text-zinc-100">
                    {it.employee_name}
                    <span className="text-[10px] text-zinc-500 ml-1">· {it.role}</span>
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-400 font-mono">{(it.remaining_now || 0).toFixed(1)}</td>
                  <td className="px-3 py-2 text-right text-zinc-500 font-mono">{(it.entitlement_total || 0).toFixed(0)}</td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number" step="0.5" min="0"
                      value={draft}
                      onChange={(e) => setDrafts({ ...drafts, [it.employee_id]: e.target.value })}
                      data-testid={`hr-migration-input-${it.employee_id}`}
                      className={`w-24 text-right px-2 py-1 bg-zinc-900 border rounded text-zinc-100 font-mono ${changed ? "border-amber-500" : "border-zinc-700"}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Shared building blocks ────────────────────────────────────────────────
function KPI({ label, value, icon: Icon, tone, testid }) {
  const toneCls = {
    emerald: "text-emerald-400",
    rose: "text-rose-400",
    sky: "text-sky-400",
    zinc: "text-zinc-200",
  }[tone] || "text-zinc-200";
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-zinc-500">
        {Icon && <Icon className={`w-3.5 h-3.5 ${toneCls}`} />}
        {label}
      </div>
      <div className={`mt-1 font-display text-2xl font-bold ${toneCls}`}>{value}</div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center text-zinc-500 p-12 gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Lade…
    </div>
  );
}
